import type { GetSitrepsResponse } from 'components/Reports/reports.types';

const scopeKey = (sitrep: GetSitrepsResponse) =>
  sitrep.incident_id || sitrep.aor_id;

const dateMs = (value: string | Date | null | undefined) => {
  if (!value) return 0;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : 0;
};

/**
 * Current is the most recently approved published report per incident/AOR.
 * Draft `timestamp` is only a fallback for legacy reports with no approval
 * time.
 */
export function publishedAtMs(sitrep: GetSitrepsResponse | undefined) {
  if (!sitrep) return 0;
  const fromApprovals = (sitrep.approvals || [])
    .map((approval) => dateMs(approval.approved_at))
    .filter((ms) => ms > 0);
  return (
    dateMs(sitrep.approved_at) ||
    (fromApprovals.length ? Math.max(...fromApprovals) : 0) ||
    dateMs(sitrep.updated_at) ||
    dateMs(sitrep.timestamp)
  );
}

/** Inclusive published-time window. A blank bound leaves that side open. */
export function sitrepInPublishedRange(
  sitrep: GetSitrepsResponse,
  start: string | null | undefined,
  end: string | null | undefined
): boolean {
  const published = publishedAtMs(sitrep);
  const startMs = start ? new Date(start).getTime() : Number.NaN;
  const endMs = end ? new Date(end).getTime() : Number.NaN;
  if (Number.isFinite(startMs) && published < startMs) return false;
  if (Number.isFinite(endMs) && published > endMs) return false;
  return true;
}

/** Current is the latest non-archived published report per incident/AOR. */
export function partitionPublishedSitreps(sitreps: GetSitrepsResponse[]): {
  latestSitreps: Map<string, GetSitrepsResponse>;
  historicalSitreps: GetSitrepsResponse[];
} {
  const latestSitreps = sitreps.reduce((latestReps, currentRep) => {
    if (currentRep.archived) return latestReps;
    const relevantId = scopeKey(currentRep);
    if (!relevantId) return latestReps;
    if (publishedAtMs(currentRep) > publishedAtMs(latestReps.get(relevantId))) {
      latestReps.set(relevantId, currentRep);
    }
    return latestReps;
  }, new Map<string, GetSitrepsResponse>());

  const historicalSitreps = sitreps
    .filter((sitrep) => {
      const relevantId = scopeKey(sitrep);
      if (!relevantId) return false;
      if (sitrep.archived) return true;
      return sitrep.id !== latestSitreps.get(relevantId)?.id;
    })
    .sort((a, b) => publishedAtMs(b) - publishedAtMs(a));

  return { latestSitreps, historicalSitreps };
}
